import { memo } from "react"
import { useTranslation } from "react-i18next"
import DOMPurify from "dompurify"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { HealthDataCardSession } from "@/modules/patients/detail/health-information"
import { MedicationCopilots } from "@/modules/patients/detail/health-information/medication/medication-copilots"
import { ChMedicationDrawer } from "@/modules/patients/detail/medication/ch/ch-medication-drawer"
import { DeMedicationDrawer } from "@/modules/patients/detail/medication/de/de-medication-drawer"
import { useGetMedicationVerification } from "@/modules/patients/detail/medication/hooks/useGetMedicationVerification"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { isFreeTextMedicationType } from "@/modules/patients/detail/medication/lib/is-free-text-medication-type"
import { useChMedicationContext } from "@/modules/patients/detail/medication/store/useChMedicationStore"
import { useDeMedicationContext } from "@/modules/patients/detail/medication/store/useDeMedicationStore"
import { MEDICATION_MODES } from "@/modules/patients/detail/medication/types/medication-mode"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

const MedicationDrawerName = "MedicationDrawer"

interface MedicationProps {
  patientId: string
}

const Medication = memo(({ patientId }: MedicationProps) => {
  const { t } = useTranslation()
  const countryCode = useCountryParam()
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const setDeMedicationDrawerMode = useDeMedicationContext((state) => state.setDeMedicationDrawerMode)
  const setChMedicationDrawerMode = useChMedicationContext((state) => state.setChMedicationDrawerMode)
  const appointmentId = useAppointmentParam()
  const { activeMedications, inactiveMedications } = useMedications()
  const { medicationConfirmationNeeded } = useGetMedicationVerification()

  const openMedicationDrawer = () => {
    if (countryCode === "DE") {
      openDrawer(() => <DeMedicationDrawer />, MedicationDrawerName, {
        onBeforeOpen: () => setDeMedicationDrawerMode(MEDICATION_MODES.VIEW),
      })
    } else if (countryCode === "CH") {
      openDrawer(() => <ChMedicationDrawer />, MedicationDrawerName, {
        onBeforeOpen: () => setChMedicationDrawerMode(MEDICATION_MODES.VIEW),
      })
    }
  }

  return (
    <HealthDataCardSession
      title={t("patient.healthInformation.medication.title")}
      sectionName="medication"
      analyticsSectionName={`healthInformation_${countryCode.toLowerCase()}_medication`}
      disabled={drawerName === MedicationDrawerName}
      editHandler={openMedicationDrawer}
      canVerify={medicationConfirmationNeeded && !!appointmentId}
      bodyContentClassName="grid grid-cols-1 grid-rows-1 gap-4">
      <MedicationCopilots
        onOpenMedicationDrawer={openMedicationDrawer}
        patientId={patientId}
        currentMedications={[...activeMedications, ...inactiveMedications]}
      />
      <MedicationCardBody />
    </HealthDataCardSession>
  )
})

const MedicationCardBody = () => {
  const { t } = useTranslation()
  const { activeMedications, inactiveMedications } = useMedications()
  const { medicationVerificationData } = useGetMedicationVerification()
  const verificationStatus = medicationVerificationData?.lastVerifyMedicationStatus
  const hasMedications = activeMedications.length > 0 || inactiveMedications.length > 0

  if (hasMedications) {
    return (
      <div className="flex w-full flex-wrap items-center gap-2">
        {activeMedications.map((medication) => (
          <Tag
            data-testid={`active-medication-tag-${medication.id}`}
            key={medication.id}
            // oxlint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                joinStrings([medication.name, "composition" in medication ? (medication.composition ?? "") : ""]),
              ),
            }}
            className={cn("max-w-full", isFreeTextMedicationType(medication.type) && "italic")}
          />
        ))}
        {inactiveMedications.map((medication) => (
          <Tag
            data-testid="inactive-medication-tag"
            key={medication.id}
            // oxlint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                joinStrings([medication.name, "composition" in medication ? (medication.composition ?? "") : ""]),
              ),
            }}
            className={cn("text-on-surface-disabled max-w-full", isFreeTextMedicationType(medication.type) && "italic")}
          />
        ))}
      </div>
    )
  }

  if (!hasMedications && verificationStatus === "NONE")
    return (
      <span
        className="text-on-surface-subdued min-h-5 px-1.5 py-1 text-xs leading-4 font-normal tracking-[0.25px]"
        data-testid="condition-none-tag">
        {t("patient.healthInformation.none")}
      </span>
    )

  return null
}

Medication.displayName = "Medication"
export { Medication }
